import { ApolloClient, createHttpLink, InMemoryCache } from "@apollo/client/core";
import { config, envIsLocalOrDev } from "@/config";

import { setContext } from "@apollo/client/link/context";
import { fetchAuthSession as authFetchAuthSession } from "aws-amplify/auth";
import { SentryLink } from "apollo-link-sentry";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";

if (envIsLocalOrDev) {
  loadDevMessages();
  loadErrorMessages();
}

// HTTP connection to the API
const httpLink = createHttpLink({
  // You should use an absolute URL here
  uri: config.moneyApi.url,
});

// https://www.apollographql.com/docs/react/networking/authentication/
const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: await getAuthToken(),
    },
  };
});

const getAuthToken = async () => {
  try {
    const session = await authFetchAuthSession();
    const accessToken = session?.tokens?.accessToken?.toString();
    if (accessToken) {
      return `Bearer ${accessToken}`;
    }
  } catch (e) {
    console.log("error in getting auth token: ", e);
  }
};

const replaceWithIncoming = (_existing, incoming) => incoming;

// AccountEntry has no id, so Apollo cannot merge list updates.
const loanApplicationEntryFieldPolicies = {
  assets: { merge: replaceWithIncoming },
  expenses: { merge: replaceWithIncoming },
  income: { merge: replaceWithIncoming },
  liabilities: { merge: replaceWithIncoming },
  repayments: { merge: replaceWithIncoming },
};

// Cache implementation
const cache = new InMemoryCache({
  typePolicies: {
    AppSecrets: {
      merge: true,
    },
    Card: {
      keyFields: ["token"],
    },
    Customer: {
      fields: {
        preferences: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
    LoanApplication: {
      fields: loanApplicationEntryFieldPolicies,
    },
  },
  possibleTypes: {
    CustomerDocument: [
      "GenericDocument",
      "PayslipDocument",
      "DriversLicenceDocument",
      "MedicareDocument",
      "PassportDocument",
      "CreditScoreDocument",
    ],
  },
});

const sentryLink = new SentryLink();

// Create the apollo client
export const apolloClient = new ApolloClient({
  link: sentryLink.concat(authLink).concat(httpLink),
  cache,
});

export const gqlOutcome = Object.freeze({
  ACCEPTED: "ACCEPTED",
});
